import React, { useContext } from "react";
import { Theme, createTheme } from "@mui/material/styles";
import isEmpty from "lodash.isempty";
import variables from "../ui/widgets/scss/variables/colors.module.scss";
import { buttonClasses, ThemeProvider } from "@mui/material";
import { ProductContext } from "../context/product-context/productContext";
import { ProductFamily } from "../context/product-context/product-family";
import { Product } from "../context/product-context/product";

export const breakpointValues = {
  xs: 0,
  sm: 375,
  md: 768,
  lg: 1016,
  xl: 1440,
};

type FamilySuffix = "swissSme" | "csDuo";

function toFamilySuffix(family: ProductFamily["name"]): FamilySuffix {
  switch (family) {
    case "SWISS_SME":
      return "swissSme";
    case "CS_DUO":
    default:
      return "csDuo";
  }
}

function extractGenericColorVariables(availableKeys: string[]): {
  [x: string]: string;
} {
  return availableKeys
    .filter((key) => key.indexOf(`products-`) === -1)
    .map((key) => ({
      [key]: variables[key as keyof typeof variables],
    }))
    .reduce((value, acc) => ({ ...acc, ...value }), {});
}

function extractProductColorVariables(
  availableKeys: string[],
  productFamily: string,
): { [x: string]: string } {
  return availableKeys
    .filter((key) => key.indexOf(`products-${productFamily}-`) === 0)
    .map((key) => {
      const strippedDownKey = key.replace(`products-${productFamily}-`, "");
      return {
        [strippedDownKey]: variables[key as keyof typeof variables],
      };
    })
    .reduce((value, acc) => ({ ...acc, ...value }), {});
}

export const genericVariables = extractGenericColorVariables(
  Object.keys(variables),
);

export const createThemeForProductFamily = (
  productFamily: FamilySuffix,
): Theme | undefined => {
  const availableKeys = Object.keys(variables);
  const genericVariables = extractGenericColorVariables(availableKeys);
  const productFamilyVariables = extractProductColorVariables(
    availableKeys,
    productFamily,
  );

  const themeVariables = { ...genericVariables, ...productFamilyVariables };

  if (isEmpty(themeVariables)) {
    return;
  }
  const colorsPrimary = {
    main: themeVariables.primary,
    text: themeVariables.primaryText,
    border: themeVariables.primaryBorder,
    hover: themeVariables.primaryHover,
    textHover: themeVariables.primaryTextHover,
    borderHover: themeVariables.primaryBorderHover,
    linkHover: themeVariables.primaryLinkHover,
  };

  const colorsSecondary = {
    main: themeVariables.secondary,
    text: themeVariables.secondaryText,
    border: themeVariables.secondaryBorder,
    hover: themeVariables.secondaryHover,
    textHover: themeVariables.secondaryTextHover,
    borderHover: themeVariables.secondaryBorderHover,
  };

  const colorsBasic = {
    main: themeVariables.primary,
    mainTwo: themeVariables.primaryTwo,
    white: themeVariables.white,
    black: themeVariables.black,
    error: themeVariables.error,
    text: themeVariables.text,
    footer: themeVariables.footerBG,
    header: themeVariables.headerTitle,
  };

  return createTheme({
    breakpoints: { values: breakpointValues },

    palette: {
      primary: {
        main: colorsBasic.main,
      },
      error: {
        main: colorsBasic.error,
      },
    },

    components: {
      MuiCircularProgress: {
        styleOverrides: {
          circle: {
            color: colorsBasic.main,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            "&:active": {
              boxShadow:
                "0px 7px 0px -11px rgba(0,0,0,0.2), 0px 0px 7px 2px rgba(0,0,0,0.14), 0px 1px 20px 0px rgba(0,0,0,0.12)",
            },
          },
          primary: {
            backgroundColor: colorsPrimary.main,
            "&:hover": {
              backgroundColor: colorsPrimary.hover,
            },
          },
        },
      },

      MuiCardActions: {
        styleOverrides: {
          root: {
            "& .scd-floating-action-container:hover .scd-floating-action-button":
              {
                backgroundColor: colorsPrimary.hover,
              },
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          root: {
            "& .scd-icon-wrapper .icon-scd-check": {
              color: colorsBasic.main,
            },
          },
        },
      },

      //  --- Language Switcher ---  //
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colorsBasic.mainTwo,
          },
        },
      },

      //  --- Button ---  //
      MuiButton: {
        styleOverrides: {
          root: {
            border: "2px solid",
            transition: "none",

            "&.scd-button-default": {
              "&&:hover": {
                backgroundColor: colorsPrimary.hover,
                borderColor: colorsPrimary.hover,
                color: colorsBasic.white,
              },
            },
          },
          contained: {
            [`&.${buttonClasses.colorPrimary}:not([disabled])`]: {
              backgroundColor: colorsPrimary.main,
              borderColor: colorsPrimary.border,
              color: colorsPrimary.text,

              "&&:hover": {
                backgroundColor: colorsPrimary.hover,
                borderColor: colorsPrimary.borderHover,
              },
            },
            [`&.${buttonClasses.colorSecondary}:not([disabled])`]: {
              backgroundColor: colorsSecondary.main,
              borderColor: colorsSecondary.border,
              color: colorsSecondary.text,

              "&&:hover": {
                backgroundColor: colorsSecondary.hover,
                borderColor: colorsSecondary.borderHover,
                color: colorsSecondary.textHover,
              },
            },
          },
        },
      },

      //  ---  Link ---  //
      MuiLink: {
        styleOverrides: {
          root: {
            color: colorsPrimary.main,
            textDecoration: "none",

            "& .scd-link-icon svg": {
              fill: colorsPrimary.main,
            },

            "&:hover": {
              color: colorsPrimary.linkHover,

              "& .scd-link-icon svg": {
                fill: colorsPrimary.linkHover,
              },
            },
          },
        },
      },

      //  --- Checkbox ---  //
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "& .scd-checkbox-checked": {
              borderColor: colorsSecondary.border,
            },

            "& .scd-checkbox-checked > svg > .st0": {
              fill: colorsSecondary.border,
            },

            "& .scd-checkbox-checked > svg > .st1": {
              fill: colorsBasic.white,
            },

            "& .scd-checkbox-unchecked > svg > .st0": {
              fill: colorsBasic.white,
            },
          },
        },
      },

      // --- Without component --- //
      MuiGrid: {
        styleOverrides: {
          root: {
            //
            // --- Header --- //
            "& .scd-flow-header-title": {
              color: colorsBasic.header,
            },
            "& .scd-header-link:hover": {
              color: colorsPrimary.linkHover,
            },
            //
            // --- Footer --- //
            "&.scd-page-footer-top": {
              background: colorsBasic.footer,
            },
            //
            // --- Radio Buttons --- //
            "& .scd-radiobutton-container-error": {
              "&:not(.scd-radiobutton-container-disabled)": {
                "& .scd-radiobutton-label": {
                  color: colorsBasic.error,
                },
                "& .scd-radiobutton-button-wrapper .scd-radiobutton-button-text":
                  {
                    color: colorsBasic.error,
                  },
              },
            },
            "& .scd-radiobutton-container": {
              "& .scd-radiobutton-label": {
                marginTop: "15px",
              },
            },
            //
            // --- FAB --- //
            "& .scd-floating-action-container:hover .scd-floating-action-button":
              {
                background: colorsPrimary.hover,
              },
          },
        },
      },

      // --- Inputs -- //
      MuiInputAdornment: {
        styleOverrides: {
          positionEnd: {
            marginLeft: 0,
          },
          positionStart: {
            marginRight: 0,
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: {
            //Labels
            "&.scd-text-field-wrapper-error .scd-text-field-label": {
              color: colorsBasic.error,
            },
            "&.scd-text-field-wrapper-error .scd-text-field-label-focused": {
              color: colorsBasic.error,
            },
            "&.scd-dropdown-wrapper-error .scd-dropdown-label": {
              color: colorsBasic.error,
            },
            "&.scd-dropdown-wrapper-error:not(.scd-dropdown-wrapper-disabled)":
              {
                "& .scd-dropdown-input .scd-dropdown-select-icon": {
                  color: colorsBasic.error,
                },
                "& .scd-dropdown-input .scd-dropdown-select-menu": {
                  borderBottomColor: colorsBasic.error,
                },
                "& .scd-dropdown-label.scd-dropdown-shrinked-label-focused + .scd-dropdown-input .scd-dropdown-select-menu":
                  {
                    borderColor: colorsBasic.error,
                  },
              },
            //Icons
            "&.scd-text-field-wrapper-error": {
              "& .icon-scd-search": {
                color: colorsBasic.error,
              },
              "& .icon-scd-calendar": {
                color: colorsBasic.error,
              },
              "& .scd-password-icon .icon-scd-password-visible": {
                color: colorsBasic.error,
              },
              "& .scd-password-icon .icon-scd-password-invisible": {
                color: colorsBasic.error,
              },
            },
            // Phone Border
            "&.scd-text-field-wrapper-error .scd-phone-masked-input": {
              color: colorsBasic.error,
              borderBottomColor: colorsBasic.error,

              "&:focus": {
                borderColor: colorsBasic.error,
              },
            },
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              "background-color": colorsBasic.main,
              color: "white",
            },
            "&.Mui-selected:hover": {
              "background-color": colorsBasic.main,
              color: "white",
            },
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          input: {
            "&.scd-text-field-input-error": {
              color: colorsBasic.error,
              borderBottomColor: colorsBasic.error,
              zIndex: 10,

              "&:focus": {
                borderColor: colorsBasic.error,
              },
            },

            "&.scd-text-field-input-error.scd-phone-masked-input": {
              color: colorsBasic.error,
              borderBottomColor: colorsBasic.error,

              "&:focus": {
                borderColor: colorsBasic.error,
              },
            },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            "&.scd-text-field-helper-error": {
              color: colorsBasic.error,
            },
            "&.scd-dropdown-error": {
              color: colorsBasic.error,
            },
          },
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: {
            "&.scd-accordion-expanded": {
              borderColor: colorsBasic.main,

              "& + .scd-accordion": {
                borderTop: `1px solid ${colorsBasic.main}`,
              },
            },
            "&:hover": {
              borderTop: `1px solid ${colorsBasic.main}`,
              borderBottom: `1px solid ${colorsBasic.main}`,

              "& + .scd-accordion": {
                borderTop: `1px solid ${colorsBasic.main}`,
              },
            },
          },
        },
      },

      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            "&:hover": {
              "& .scd-typography-wrapper, & .scd-accordion-summary-icon": {
                color: colorsBasic.main,
              },
            },
            "&.scd-accordion-summary-expanded": {
              "& .scd-typography-wrapper": {
                color: colorsBasic.main,
              },

              "& .scd-accordion-summary-icon": {
                color: colorsBasic.main,
              },
            },
          },
        },
      },
    },
  });
};

export const OnboardingTheme = {
  genericVariables,
};

export const resolveThemeProductFamily = (
  product: Product,
): ProductFamily["name"] => {
  if (product === undefined) {
    return "CS_DUO";
  }

  return product?.family?.name;
};

export default function CommercialThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { selectedProduct } = useContext(ProductContext);

  return (
    <ThemeProvider
      theme={createThemeForProductFamily(
        toFamilySuffix(resolveThemeProductFamily(selectedProduct)),
      )}
    >
      {children}
    </ThemeProvider>
  );
}
